import { useEffect, useState } from "react";
import type { ShopCatalogResponse, ShopProductSummary } from "@gooner/shared";
import { userApi } from "./api";
import { useSession } from "./session";

/**
 * The shop's catalog as this viewer sees it. It cannot live on /api/settings like the Feed's
 * switch: a preview account sees the shop while everyone else does not, so the answer depends
 * on the viewer. Asked once per page load per account, and shared by every caller.
 */
const catalogs = new Map<string, Promise<ShopCatalogResponse | null>>();

const catalogFor = (userId: string) => {
  let answer = catalogs.get(userId);
  if (!answer) {
    answer = userApi.shopCatalog().then((r) => r, () => null);
    catalogs.set(userId, answer);
  }
  return answer;
};

/** The catalog, or null while it loads, when the shop is closed, or when it failed. */
export function useShopCatalog(): { ready: boolean; catalog: ShopCatalogResponse | null } {
  const userId = useSession((s) => s.user?.id ?? "");
  const [state, setState] = useState<{ ready: boolean; catalog: ShopCatalogResponse | null }>({ ready: false, catalog: null });
  useEffect(() => {
    let cancelled = false;
    void catalogFor(userId).then((c) => !cancelled && setState({ ready: true, catalog: c?.open ? c : null }));
    return () => {
      cancelled = true;
    };
  }, [userId]);
  return state;
}

/** Is the shop open to whoever is looking? The menu's question. */
export function useShopOpen(): boolean {
  return useShopCatalog().catalog !== null;
}

/**
 * One product to promote, picked once per mount: a random Featured product, else the first
 * one listed. `ready` is false until the catalog answers, so a caller can wait rather than
 * show one card and then swap to another.
 */
export function useShopPromo(): { ready: boolean; product: ShopProductSummary | null } {
  const { ready, catalog } = useShopCatalog();
  const [draw] = useState(() => Math.random());
  if (!catalog) return { ready, product: null };
  const live = catalog.products.filter((p) => !p.soldOut && p.images.length > 0);
  const featured = live.filter((p) => p.category === "featured");
  const pool = featured.length > 0 ? featured : live.slice(0, 1);
  return { ready, product: pool.length > 0 ? (pool[Math.floor(draw * pool.length)] ?? null) : null };
}
